import { HttpStatusCode } from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";

import { CATEGORY_ALIAS } from "../../types/talk";

import { findCategoryForContext } from "../../libs/category";
import { getReadable, getWritable, onLikePost } from "../../libs/talk";
import { isArrayEmpty } from "../../libs/common";

import {
  getTalkPostList,
  getTalkPostListGroup,
  TalkCategory,
  TalkGroupPostItem,
  TalkListItem,
} from "../../services/talkServices";
import { useSandiTalk } from "../../contexts/boardContext";
import {
  BannerItem,
  getBanner,
  LinkLocationType,
} from "../../services/bannerServices";
import { useUserContext } from "../../contexts/userContext";

import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../hooks/useInfiniteScroll";
import useFullScreenLoading from "../../hooks/useFullScreenLoading";
import useSimplePopup from "../../hooks/useSimplePopup";

import StickyHeader from "../../components/Layout/StickyHeader";
import SearchHeader from "../../components/SearchHeader";
import BannerSwiper from "../../components/Swiper/Banner";
import BoardTab from "../../components/Talk/BoardTab";
import HorizontalRule from "../../components/Rule/Horizontal";
import PostList from "../../components/Talk/PostList";
import PopularPostList, {
  SelectTab,
} from "../../components/Talk/PostList/Popular";
import TalkSummaryListView from "../../components/Talk/SummaryList";
import { SelectOption } from "../../components/Select";
import FindBuildingPopup from "../../components/Popup/FindBuilding";
import SimplePopup from "../../components/Popup/Simple";
import CommonProfile from "../../components/MyInfo/Profile";

import talkHeaderBg from "../../assets/img/talk-man.png";
import { Reason } from "../../types/location";

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function Talk(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const TALK_PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;
  const [user] = useUserContext();
  const [talk, { updateCategory, updateField, resetStoreId }] = useSandiTalk();
  const [postList, setPostList] = useState<TalkListItem[]>([]);
  const [postGroupList, setPostGroupList] = useState<TalkGroupPostItem[]>([]);
  const scrollDivRef = React.useRef<HTMLDivElement>(null);
  const [rollBanner, setRollBanner] = useState<BannerItem[]>([]);
  const [isPostLoading, setIsPostLoading] = useState<boolean>(false);
  const [isBannerLoading, setIsBannerLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({
    isLoading: isPostLoading || isBannerLoading || (talk.isLoading ?? false),
    testId: "talk",
  });
  const [applyLocate, setApplyLocate] = useState<boolean>(false);
  const [simplePopupComponent, { openError }] = useSimplePopup({});
  const reason = Reason.BUILDING_CHANGE;

  const buildingCategoryOption: SelectOption[] = [
    { label: "이벤트", value: CATEGORY_ALIAS.BUILDING_EVENT },
  ];
  const [selectedBuildingCategory, setSelectedBuildingCategory] = useState<
    string | undefined
  >(CATEGORY_ALIAS.BUILDING_EVENT);
  const [popup, setPopup] = useState<"" | "selectBuilding" | "failedLoadPost">(
    ""
  );

  const handleGotoCategory = (categoryAlias: string) => {
    updateCategory({
      categoryInfo:
        findCategoryForContext(
          talk?.categoryList ?? [],
          "alias",
          categoryAlias,
          0
        ) ?? {},
    });
  };

  const loadPostList = async (args?: InfiniteScrollCallbackArgs) => {
    let itemLength = INFINITE_CODE.ERROR;
    if (isPostLoading && postList.length > 0) return itemLength;
    if (talk.isLoading === true) {
      return itemLength;
    }
    if (location.state?.lifeCategoryId && applyLocate === false) {
      return itemLength;
    }

    setIsPostLoading(true);
    let additionalParams = "";
    if (talk.sort) {
      additionalParams += `&sort=${talk.sort}`;
    } else {
      additionalParams += "&sort=viewCount.desc";
    }
    if (talk?.dateRange) {
      additionalParams += `&dateRange=${talk.dateRange}`;
    }
    if (args?.page) {
      additionalParams += `&page=${args.page}`;
    } else {
      additionalParams += `&page=${SCROLL_LOAD_START_PAGE - 1}`;
    }
    if (args?.pageSize) {
      additionalParams += `&pageSize=${args.pageSize}`;
    } else {
      additionalParams += `&pageSize=${TALK_PAGE_SIZE}`;
    }

    let url;
    if (talk.middleCategory) {
      url = talk.middleCategory?.url;
    }

    if (url?.includes(CATEGORY_ALIAS.BUILDING_MENU)) {
      if (talk.storeId) {
        additionalParams += `&storeId=${talk.storeId}`;
      } else {
        console.log("talk.storeId is null");
        setPostList([]);
        setIsPostLoading(false);
        return itemLength;
      }
    }

    if (url?.includes(CATEGORY_ALIAS.BUILDING)) {
      additionalParams += `&buildingId=${talk.buildingId}`;
    }

    if (!url) {
      setIsPostLoading(false);
      setPostList([]);
      return itemLength;
    }

    try {
      if (url?.includes("GROUP")) {
        if (talk.middleCategory?.alias === CATEGORY_ALIAS.BUILDING_ALL) {
          const result = await getTalkPostListGroup(
            url + additionalParams.replace("pageSize=10", "pageSize=5") ?? ""
          );
          if (result.status === HttpStatusCode.Ok) {
            setPostGroupList(result.data);
          }
        } else {
          const res = await getTalkPostList(
            `pinelife/post/list?category=${CATEGORY_ALIAS.TALK}&viewType=ALL_DEEP` +
              additionalParams
          );
          if (res.status === HttpStatusCode.Ok) {
            if (!args?.page || args?.page === 1) {
              await setPostList(res.data.items);
            } else {
              await setPostList((prev) => [...prev, ...res.data.items]);
            }
            itemLength = res.data.items.length ?? 0;
          }
        }
      } else {
        const res = await getTalkPostList(url + additionalParams ?? "");
        if (res.status === HttpStatusCode.Ok) {
          if (!args?.page || args?.page === 1) {
            await setPostList(res.data.items);
          } else {
            await setPostList((prev) => [...prev, ...res.data.items]);
          }
          itemLength = res.data.items.length ?? 0;
        }
      }
    } catch (e: any) {
      setPopup("failedLoadPost");
    } finally {
      setIsPostLoading(false);
    }

    return itemLength;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading:
      isPostLoading ||
      postList.length < TALK_PAGE_SIZE ||
      talk?.middleCategory?.alias === CATEGORY_ALIAS.BUILDING_ALL,
    startPage: SCROLL_LOAD_START_PAGE,
    page: SCROLL_LOAD_START_PAGE,
    pageSize: TALK_PAGE_SIZE,
    callback: async (args) => {
      return await loadPostList(args);
    },
  });

  const loadBannerList = async () => {
    setIsBannerLoading(true);
    try {
      const res = await getBanner({
        location: (talk.largeCategory?.alias?.toUpperCase() ??
          "TALK") as LinkLocationType,
        buildingId: talk.buildingId,
      });
      if (res.status === HttpStatusCode.Ok) {
        setRollBanner(res.data.items);
      } else {
        throw new Error("배너를 불러오는데 실패했습니다.");
      }
    } catch (e) {
      openError(e, "배너를 불러오는데 실패했습니다.");
    } finally {
      setIsBannerLoading(false);
    }
  };

  const isWritable = () => {
    if (talk.middleCategory === undefined) return false;

    return getWritable(talk?.middleCategory, user);
  };

  const handleLike = (postId: number) => {
    setPostList((prev) =>
      prev.map((post) =>
        post.lifePostId === postId ? onLikePost(post, user) : post
      )
    );
  };

  useEffect(() => {
    const updateCategoryFromLocation = async () => {
      const categoryInfo = findCategoryForContext(
        talk?.categoryList ?? [],
        "lifeCategoryId",
        location.state.lifeCategoryId,
        0
      );
      if (categoryInfo) {
        await updateCategory({
          categoryInfo: categoryInfo,
        });
      } else {
        console.log("categoryInfo is null");
      }
    };

    // 외부에서 게시판을 지정한 경우, 카테고리가 로드될 경우 새로 겁색한다.
    if (
      talk.isLoading === false &&
      location.state?.lifeCategoryId &&
      !isArrayEmpty(talk.categoryList)
    ) {
      updateCategoryFromLocation();
    }
  }, [talk.isLoading, location.state, talk.categoryList, talk.storeId]);

  useEffect(() => {
    if (user.isLoading) return;
    if (talk.largeCategory) {
      loadBannerList();
    } else {
      // 대분류가 존재하지않으면, 배너 정보를 가져올 수 없음.
    }
  }, [talk.largeCategory, talk.buildingId, user.isLoading]);

  useEffect(() => {
    scrollDivRef.current?.scrollIntoView();
    if (user.isLoading) return;
    resetScroll();
    loadPostList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    talk.dateRange,
    talk.middleCategory,
    talk.storeId,
    talk.buildingId,
    user.buildingId,
    user.isLoading,
    talk.sort,
    applyLocate,
  ]);

  useEffect(() => {
    if (
      applyLocate === false &&
      talk.middleCategory?.lifeCategoryId === location.state?.lifeCategoryId
    ) {
      setApplyLocate(true);
    }
  }, [talk.isLoading, talk.middleCategory]);

  return (
    <>
      {loadingComponent}
      <div className="flex flex-col gap-3" ref={scrollDivRef}>
        <StickyHeader
          title="샌디톡"
          rightContent={
            <>
              <div className="flex items-center space-x-4">
                <SearchHeader />
                <div onClick={() => navigate("/alarm")}>
                  <TalkNotificationIcon />
                </div>
                <button
                  onClick={() => {
                    navigate("/myinfo");
                  }}
                >
                  <CommonProfile
                    src={user?.profileImage?.filePath}
                    sizeVariant="like"
                  />
                </button>
              </div>
            </>
          }
          className="px-5 !fixed top-0 left-0 right-0"
        />
        <div className="h-[64px]"></div>

        <BoardTab
          tabs={
            talk.categoryList?.filter(
              (category) => category.alias !== CATEGORY_ALIAS.SANDI_INFO
            ) ?? []
          }
          type={talk.largeCategory?.alias}
        />

        {talk.largeCategory?.alias === CATEGORY_ALIAS.BUILDING ? (
          talk.middleCategory?.alias !== CATEGORY_ALIAS.BUILDING_MENU && (
            <div className="px-4 text-3xl flex flex-col gap-3 py-7">
              <div
                className="flex gap-5 border-b-2 border-black w-fit items-center"
                onClick={() => {
                  setPopup("selectBuilding");
                }}
              >
                <span className="truncate max-w-[calc(100vw-80px)]">
                  {talk.buildingName}
                </span>
                <TalkFindBuildingDownArrow />
              </div>
              <div>방문을 환영합니다.</div>
            </div>
          )
        ) : (
          <>
            <div className="px-5">
              <div className="flex flex-col gap-1 text-sm py-7 relative">
                좋은 하루 보내세요!
                <span className="font-bold text-3xl">{user.nickname}님!</span>
                <img
                  src={talkHeaderBg}
                  alt="talk_man_bg"
                  className="h-[240px] w-[140px] absolute right-2.5"
                />
              </div>
            </div>

            {isBannerLoading || rollBanner.length === 0 ? (
              <div className="px-5 z-10">
                <Skeleton className="h-40 rounded-xl" />
                <div className="flex pt-2.5 justify-center">
                  <Skeleton className="w-8 rounded-full h-1.5" />
                </div>
              </div>
            ) : (
              <div className="pb-4" data-testid="talk-roll-banner">
                <BannerSwiper banners={rollBanner} position="TALK" />
              </div>
            )}
          </>
        )}

        {talk.middleCategory?.alias !== CATEGORY_ALIAS.BUILDING_MENU && (
          <HorizontalRule
            thickness={6}
            margin={
              talk.largeCategory?.alias === CATEGORY_ALIAS.TALK
                ? "1.5rem 0px 0px 0px"
                : "0px 0px"
            }
            className="bg-brand-neutral-05"
          />
        )}

        {/* 게시글 리스트 출력 분기 */}
        {talk.middleCategory?.alias === CATEGORY_ALIAS.BUILDING_ALL && (
          <div className="pb-28">
            <div className="pb-8">
              <BannerSwiper banners={rollBanner} position="BUILDING" />
            </div>
            <div className="px-5 flex py-2 justify-between items-center">
              <div className="text-xl font-semibold">공지</div>
              <div
                onClick={() => {
                  handleGotoCategory(CATEGORY_ALIAS.BUILDING_NOTICE);
                }}
              >
                <ViewMoreIcon />
              </div>
            </div>
            <TalkSummaryListView
              defaultPostList={
                postGroupList.find(
                  (list) => list.alias === CATEGORY_ALIAS.BUILDING_NOTICE
                )?.lifePostList
              }
            />
            <div className="px-5 flex py-2 justify-between items-center">
              <div className="text-xl font-semibold">
                <SelectTab
                  value={
                    selectedBuildingCategory ?? buildingCategoryOption[0].value
                  }
                  options={buildingCategoryOption}
                  onClick={(selectedOption: SelectOption) => {
                    setSelectedBuildingCategory(selectedOption.value);
                  }}
                />
              </div>
              <div
                onClick={() => {
                  handleGotoCategory(
                    selectedBuildingCategory ?? buildingCategoryOption[0].value
                  );
                }}
              >
                <ViewMoreIcon />
              </div>
            </div>
            <TalkSummaryListView
              defaultPostList={
                postGroupList.find(
                  (list) => list.alias === selectedBuildingCategory
                )?.lifePostList
              }
            />
            {postGroupList
              .filter(
                (item) =>
                  ![
                    CATEGORY_ALIAS.BUILDING_NOTICE,
                    CATEGORY_ALIAS.BUILDING_EVENT,
                  ].includes(item.alias)
              )
              .filter((item) => {
                return getReadable(item as TalkCategory, user);
              })
              .map((group) => (
                <div key={group.lifeCategoryId}>
                  <div className="px-5 flex py-2 justify-between items-center">
                    <div className="text-xl font-semibold">{group.name}</div>
                    <div
                      onClick={() => {
                        handleGotoCategory(group.alias);
                      }}
                    >
                      <ViewMoreIcon />
                    </div>
                  </div>
                  <TalkSummaryListView defaultPostList={group.lifePostList} />
                </div>
              ))}
          </div>
        )}
        {talk.middleCategory?.alias === CATEGORY_ALIAS.TALK_POPULAR && (
          <PopularPostList postList={postList} onClickLike={handleLike} />
        )}
        {talk.middleCategory?.alias !== CATEGORY_ALIAS.TALK_POPULAR &&
          talk.middleCategory?.alias !== CATEGORY_ALIAS.BUILDING_ALL && (
            <PostList
              postList={postList}
              onClickLike={handleLike}
              storeList={talk.middleCategory?.storeList ?? []}
            />
          )}

        <div className="fixed bottom-24 right-5 flex flex-col gap-2.5 z-[19]">
          <div
            className="border border-brand-floating-secondary-button rounded-[10px] p-2 bg-white"
            onClick={() => {
              scrollDivRef.current?.scrollIntoView({ behavior: "smooth" });
            }}
            style={{ boxShadow: "2px 5px 4px 0px rgba(0, 0, 0, 0.10)" }}
          >
            <GoToTop />
          </div>
          {isWritable() && (
            <div
              className=" bg-brand-floating-primary-button p-2 rounded-[10px] h-[40px] w-[40px]"
              onClick={() => navigate("/talk/newPost")}
              style={{ boxShadow: "2px 5px 4px 0px rgba(236, 30, 159, 0.10)" }}
            >
              <NewPostIcon />
            </div>
          )}
        </div>
      </div>

      <FindBuildingPopup
        popup={popup}
        onClose={() => setPopup("")}
        onChangeBuilding={(building) => {
          updateField({
            buildingId: building.buildingId,
            buildingName: building.buildingName,
          });
          resetStoreId();
        }}
        type={
          talk?.middleCategory?.alias === CATEGORY_ALIAS.BUILDING_MENU
            ? "CAFETERIA"
            : "BUILDING_BBS"
        }
        reason={reason}
      />

      <SimplePopup
        isOpen={popup === "failedLoadPost"}
        onClose={() => {
          setPopup("");
          loadPostList();
        }}
        message={
          "게시글을 불러오는데 실패했습니다.\n잠시 후 다시 시도해주세요."
        }
      />
      {simplePopupComponent}
    </>
  );
}

export type SkeletonProps = {
  className?: string;
  placeHolder?: string;
};

export const Skeleton = ({ className, placeHolder }: SkeletonProps) => {
  return (
    <div className={clsx(["bg-white-50 w-full", className])}>
      <div className="animate-pulse">
        <div className={clsx(["bg-neutral-200 w-full", className])}>
          <div className="px-2 text-xs overflow-hidden">{placeHolder}</div>
        </div>
      </div>
    </div>
  );
};

export const TalkFindBuildingDownArrow = () => (
  <svg
    width="16"
    height="10"
    viewBox="0 0 16 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8 10L-8.74228e-07 1.39876e-06L16 0L8 10Z" fill="black" />
  </svg>
);

export const ViewMoreIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99899 2.91507V13.0849"
      stroke="#7A7A7A"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M2.91406 8H13.0839"
      stroke="#7A7A7A"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const NewPostIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.0721 3.0625L20.9375 7.92786L7.92786 20.9375L3.06707 20.9329L3.0625 16.0721L16.0721 3.0625Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.7891 11.0081L12.9665 7.18548"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export const GoToTop = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 19L12 6.26998"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M5 12L12 5L19 12"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const TalkNotificationIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.17578 16.0723H20.8225M5.21194 16.0723V9.28513C5.21194 5.53666 8.25068 2.49792 11.9991 2.49792C15.7476 2.49792 18.7863 5.53666 18.7863 9.28513V16.0723M9.28426 18.1085V18.7872C9.28426 20.2866 10.4998 21.5021 11.9991 21.5021C13.4985 21.5021 14.714 20.2866 14.714 18.7872V18.1085"
      stroke="#1D1929"
      strokeWidth="1.75"
    />
  </svg>
);

export const TalkProfileDefault = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="16" fill="#E7E7E8" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.5737 12.6265C18.5737 14.0586 17.4123 15.2193 15.9793 15.2193C14.5462 15.2193 13.3848 14.0586 13.3848 12.6265C13.3848 11.1944 14.5462 10.0337 15.9793 10.0337C17.4123 10.0337 18.5737 11.1944 18.5737 12.6265Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.8141 21.2917H11.1426C11.1426 21.2624 11.1426 21.4497 11.1426 20.8581C11.1426 19.2557 12.4416 17.9572 14.044 17.9572H17.9126C19.5151 17.9572 20.8141 19.2557 20.8141 20.8581C20.8141 21.4497 20.8141 21.2624 20.8141 21.2917Z"
      fill="#B7B7B8"
      stroke="#B7B7B8"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
  </svg>
);
